import { StorageService } from '@/services/core/storage'

const API_BASE = import.meta.env.VITE_ADNUNTIUS_API_BASE || 'https://api.dev.adnuntius.com/api'
const TOKEN_KEY = 'adn-auth-token'
const IS_LOCAL_API = ['localhost', '127.0.0.1'].includes(new URL(API_BASE).hostname)

function passwordGrant(username: string, password: string, masqueradeAsUser?: string) {
  const params = new URLSearchParams({
    grant_type: 'password',
    scope: 'ng_api',
    username,
    password,
  })
  if (masqueradeAsUser) params.set('masqueradeAsUser', masqueradeAsUser)
  return params
}

export interface AuthToken {
  access_token: string
  token_type: string
  expires_in: string
  refresh_token: string
}

export interface TwoFactorChallenge {
  twoFactor: true
  authToken: string
  type: string
}

export type LoginResult = AuthToken | TwoFactorChallenge

export function isTwoFactorChallenge(r: LoginResult): r is TwoFactorChallenge {
  return 'twoFactor' in r
}

export const AuthService = {
  login: async (username: string, password: string): Promise<LoginResult> => {
    const response = await fetch(`${API_BASE}/authenticate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: passwordGrant(username, password).toString(),
    })

    if (response.status === 401) {
      const body = await response.json()
      if (body?.scope === 'TWO_FACTOR_AUTH' || body?.scope === 'FORCED_TWO_FACTOR_AUTH') {
        return {
          twoFactor: true,
          authToken: body.access_token || body.refresh_token,
          type: body.two_factor_auth_type ?? 'APP',
        }
      }
      const message = body?.error_description || body?.error || 'Authentication failed'
      throw new Error(message)
    }

    if (!response.ok) {
      let message = 'Authentication failed'
      try {
        const body = await response.json()
        if (body?.error_description) message = body.error_description
        else if (body?.error) message = body.error
      } catch { /* ignore */ }
      throw new Error(message)
    }

    const token: AuthToken = await response.json()
    AuthService.storeToken(token)
    return token
  },

  masquerade: async (adminUsername: string, password: string, targetUsername: string): Promise<LoginResult> => {
    const response = await fetch(`${API_BASE}/authenticate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: passwordGrant(adminUsername, password, targetUsername).toString(),
    })

    const body = await response.json()

    if (body?.scope === 'TWO_FACTOR_AUTH' || body?.scope === 'FORCED_TWO_FACTOR_AUTH') {
      return {
        twoFactor: true,
        authToken: body.access_token || body.refresh_token,
        type: body.two_factor_auth_type ?? 'APP',
      }
    }

    if (response.ok && body?.access_token) {
      const token: AuthToken = body
      AuthService.storeToken(token)
      return token
    }

    const message = body?.error_description || body?.error || `Masquerade failed (${response.status})`
    throw new Error(message)
  },

  verify2fa: async (authToken: string, otpCode: string): Promise<AuthToken> => {
    const formData = new URLSearchParams()
    formData.set('twoFactorAuthCode', otpCode)

    const url = new URL(`${API_BASE}/authenticate/2fa`)
    if (!IS_LOCAL_API) url.searchParams.set('auth_token', authToken)

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        ...(IS_LOCAL_API ? { Authorization: `Bearer ${authToken}` } : {}),
      },
      body: formData.toString(),
    })

    if (!response.ok) {
      let message = 'Two-factor authentication failed'
      try {
        const body = await response.json()
        if (body?.error_description) message = body.error_description
        else if (body?.error) message = body.error
      } catch {
        // ignore parse errors
      }
      throw new Error(message)
    }

    const token: AuthToken = await response.json()
    AuthService.storeToken(token)
    return token
  },

  storeToken: (token: AuthToken) => {
    localStorage.setItem(TOKEN_KEY, JSON.stringify(token))
  },

  getToken: (): AuthToken | null => {
    const data = localStorage.getItem(TOKEN_KEY)
    return data ? JSON.parse(data) : null
  },

  getAccessToken: (): string | null => {
    return AuthService.getToken()?.access_token ?? null
  },

  clearToken: () => {
    localStorage.removeItem(TOKEN_KEY)
    // A masquerade only exists for as long as the token it was granted with,
    // so it must never outlive it and warn about a session that is gone.
    StorageService.setMasquerade(null)
  },

  refresh: async (): Promise<AuthToken> => {
    const token = AuthService.getToken()
    if (!token?.refresh_token) throw new Error('No refresh token available')

    const params = new URLSearchParams({
      grant_type: 'refresh_token',
      refresh_token: token.refresh_token,
    })

    const response = await fetch(`${API_BASE}/authenticate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: params.toString(),
    })

    if (!response.ok) {
      AuthService.clearToken()
      throw new Error('Token refresh failed')
    }

    const newToken: AuthToken = await response.json()
    AuthService.storeToken(newToken)
    return newToken
  },

  isAuthenticated: (): boolean => {
    return !!AuthService.getToken()
  },
}
